import React, { useEffect, useReducer } from 'react'
import { Prompt } from 'react-router-dom'
import { gql } from 'apollo-boost'
import { useMutation } from '@apollo/react-hooks'
import { Input, Checkbox, Radio, Button, Alert, notification } from 'antd'
import { StopOutlined, LoadingOutlined } from '@ant-design/icons'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { getDeliveryErrors, getProductName } from './utils'
import { Product, PhotoProduct } from './types'
import reducer, { initialState } from './lib/reducer'

const MIN_COMMENT_LENGTH = 4

interface Props {
  products: Product[]
  booking: {
    id: string
    isOpenDroneCategory: boolean | null

  }
}

const submitAssetDeliveriesMutation = gql`
  mutation SubmitAssetDeliveriesMutation($input: SubmitAssetDeliveriesInput!) {
    SubmitAssetDeliveries(input: $input) {
      product {
        id
        state
      }
    }
  }
`

const toggleOpenDroneCategoryMutation = gql`
  mutation ToggleOpenDroneCategoryOnBooking($id: ID!, $value: Boolean!) {
    ToggleOpenDroneCategoryOnBooking(id: $id, value: $value) {
      booking {
        id
        isOpenDroneCategory
      }
    }
  }
`

function DeliverySidebar({ products, booking }: Props) {
  const [submitAssetDeliveries] = useMutation(submitAssetDeliveriesMutation)
  const [toggleOpenDronecategory] = useMutation(toggleOpenDroneCategoryMutation)
  const [state, dispatch] = useReducer(reducer, initialState)

  const deliverableProducts = products.filter((p) => p.state === 'booked')
  const checkedProductIds = Object.keys(state.checkedProducts).filter(
    (id) => !!state.checkedProducts[id]
  )
  const deliveryErrors = getDeliveryErrors(deliverableProducts)
  const shouldConfirmRouteLeave = Object.values(deliveryErrors)
    .flat()
    .some((e) => e.name === 'PENDING')

  const hasDrone = products.some(product => product.__typename === 'DronePhotoProduct' || ('drone' in product && product.drone))
  const shouldAlertDroneCategory = booking.isOpenDroneCategory === null && hasDrone

  useEffect(() => {
    if (shouldConfirmRouteLeave) {
      window.onbeforeunload = () => true
    } else {
      window.onbeforeunload = null
    }
  }, [shouldConfirmRouteLeave])

  function handleCheckboxChange(e: CheckboxChangeEvent) {
    const { name, checked } = e.target
    if (name)
      dispatch({ type: 'SET_CHECKED_PRODUCT', productId: name, checked })
  }

  function handleCommentChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const { name, value } = e.target
    if (name) dispatch({ type: 'SET_PRODUCT_COMMENT', productId: name, value })
  }

  function productDeliveryRequiresComment(productId: string) {
    return (deliveryErrors[productId] || []).some((e) => !!e.resolveWithComment)
  }

  function productDeliveryIsValid(productId: string) {
    const errors = deliveryErrors[productId] || []
    const comment = state.productComments[productId]
    const needsComment = errors.some((e) => !!e.resolveWithComment)
    const mandatoryErrors = errors.filter((e) => !e.resolveWithComment)
    return (
      mandatoryErrors.length === 0 &&
      (!needsComment || (comment && comment.length >= MIN_COMMENT_LENGTH))
    )
  }

  function handleSubmit() {
    const deliveries = checkedProductIds.map((productId) => {
      const variables = {
        input: {
          productId,
          lowerQuantityComment: state.productComments[productId],
          editingComment: state.productCommentsForEditing[productId]
            ? state.productCommentsForEditing[productId]!.join('.\n')
            : undefined,
        },
      }

      return submitAssetDeliveries({ variables })
        .then(() => {
          // Instead of calling a dispatch for each property we want to reset,
          // we just call single one. This avoids unnecessary renders, and keeps
          // the other dispatches clean, because we don't have to allow them to
          // accept undefined values.
          dispatch({ type: 'RESET_PRODUCT', productId })
        })
        .catch(() => {
          const product = products.find((p) => p.id === productId)
          const error = product
            ? `${getProductName(
                product
              )} kunne ikke leveres. Oppdater siden og forsøk på nytt.`
            : 'En av leveransene feilet grunnet en feil. Oppdater siden og forsøk på nytt.'

          dispatch({
            type: 'SET_SUBMIT_ERRORS',
            errors: [...state.submitErrors, error],
          })
        })
    })

    dispatch({ type: 'SET_IS_SUBMITTING', isSubmitting: true })
    return Promise.all(deliveries).finally(() =>
      dispatch({ type: 'SET_IS_SUBMITTING', isSubmitting: false })
    )
  }

  const deliveryIsValid =
    checkedProductIds.length && checkedProductIds.every(productDeliveryIsValid)

  return (
    <div className="delivery-sidebar">
      <Prompt
        when={shouldConfirmRouteLeave}
        message="Leveransen er ikke komplett. Er du sikker på at du ønsker å forlate siden? Det kan hende endringene dine ikke er lagret."
      />

      <h2>Leveranse</h2>

      {state.submitErrors.length > 0 && (
        <ul className="submit-errors">
          {state.submitErrors.map((error, i) => (
            <li key={i}>{error}</li>
          ))}
        </ul>
      )}

      {shouldAlertDroneCategory && (
        <Alert className="drone-category-alert" type="warning" message="Du må spesifisere om du flyr drone i åpen kategori eller ikke før du kan fortsette." />
      )}

      {hasDrone && (
        <div className="drone-category">
          <h4>Flyr i åpen kategori:</h4>
          <Radio.Group
            buttonStyle="solid"
            value={booking.isOpenDroneCategory === null ? '' : booking.isOpenDroneCategory.toString()}
            onChange={(e) => {
              toggleOpenDronecategory({
                variables: {
                  id: booking.id,
                  value: e.target.value === 'true',
                },
              }).catch(() => {
                notification.error({
                  message: 'Kunne ikke oppdatere dronekategori',
                  description: 'Dronekategori kunne ikke oppdateres. Forsøk på nytt eller kontakt EFKT.',
                })
              })
            }}
          >
            <Radio.Button value="true">Ja</Radio.Button>
            <Radio.Button value="false">Nei</Radio.Button>
          </Radio.Group>
        </div>
      )}

      <div className="products">
        {deliverableProducts.map((product) => {
          const isChecked = !!state.checkedProducts[product.id]
          const errors = deliveryErrors[product.id]
          const commentsToDeskAreToggled = !!state
            .productCommentsForEditingToggled[product.id]
          const productCommentsForDesk =
            state.productCommentsForEditing[product.id] || []
          const availableCommentsForEditing = (product as PhotoProduct)
            .availableCommentsForEditing

          return (
            <div key={product.id} className="product">
              <Checkbox
                name={product.id}
                checked={isChecked}
                onChange={handleCheckboxChange}
              >
                {getProductName(product)}
              </Checkbox>
              {isChecked && (
                <>
                  {errors && errors.length > 0 && (
                    <ul className="product-validation errors">
                      {errors.map((error, i) => (
                        <li key={i}>
                          {error.name === 'PENDING' ? (
                            <LoadingOutlined />
                          ) : (
                            <StopOutlined />
                          )}
                          {error.message}
                        </li>
                      ))}
                    </ul>
                  )}
                  {productDeliveryRequiresComment(product.id) && (
                    <Input.TextArea
                      name={product.id}
                      value={state.productComments[product.id]}
                      onChange={handleCommentChange}
                      placeholder="Kommenter her"
                    />
                  )}
                  {!!availableCommentsForEditing &&
                    availableCommentsForEditing.length > 0 && (
                      <>
                        <h3>
                          Kommentarer til desk ({productCommentsForDesk.length})
                        </h3>
                        <Button
                          size="small"
                          onClick={() => {
                            dispatch({
                              type: 'SET_PRODUCT_COMMENTS_TO_EDITING_TOGGLED',
                              productId: product.id,
                              toggled: !state.productCommentsForEditingToggled[
                                product.id
                              ],
                            })
                          }}
                        >
                          {commentsToDeskAreToggled ? 'Skjul' : 'Vis'}
                        </Button>
                        {commentsToDeskAreToggled && (
                          <ul className="product-comments-for-desk">
                            {availableCommentsForEditing.map((comment, i) => (
                              <li key={i}>
                                <Checkbox
                                  checked={productCommentsForDesk.includes(
                                    comment as string
                                  )}
                                  onChange={(e) => {
                                    const prevComments =
                                      state.productCommentsForEditing[
                                        product.id
                                      ] || []
                                    dispatch({
                                      type: 'SET_PRODUCT_COMMENTS_FOR_EDITING',
                                      productId: product.id,
                                      comments: e.target.checked
                                        ? [...prevComments, comment as string]
                                        : prevComments.filter(
                                            (prevComment) =>
                                              prevComment !==
                                              (comment as string)
                                          ),
                                    })
                                  }}
                                >
                                  {comment}
                                </Checkbox>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                </>
              )}
            </div>
          )
        })}
      </div>

      {deliverableProducts.length > 0 && (
        <Button
          type="primary"
          loading={state.isSubmitting}
          disabled={!deliveryIsValid || state.isSubmitting || shouldAlertDroneCategory}
          onClick={handleSubmit}
        >
          Levér valgte produkter
        </Button>
      )}
    </div>
  )
}

export default DeliverySidebar
